import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    ChannelMixColors,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatChartPercent,
    formatChartMultiplier,
    formatDateLabel,
    KpiCard,
    moneyAmount,
    Panel,
    projectCurrency,
    currencyUnit,
    ReportingPageDescription,
    snapshotCompletenessAlert,
} from "./ReportingLab.jsx";

function sourceKey(source) {
    return source?.integrationName || "-";
}

function sourceLabel(source) {
    return source?.integrationName || source?.campaignName || "-";
}

function hasSeriesValues(data) {
    return Object.values(data ?? {}).some((value) => value !== null && value !== undefined);
}

function sourceData(source, basis) {
    if (!source) {
        return {};
    }

    if (basis === "completed") {
        return hasSeriesValues(source.dataNet) ? source.dataNet : (source.dataGross ?? source.data ?? {});
    }

    return hasSeriesValues(source.dataGross) ? source.dataGross : (source.dataNet ?? source.data ?? {});
}

function moneySourceData(source) {
    return source?.data ?? {};
}

function sumNumberSeriesByChannel(graphData, basis, labels) {
    const channels = new Map();

    (graphData?.dataSources ?? []).forEach((source) => {
        const key = sourceKey(source);
        const existing = channels.get(key) ?? {
            key,
            label: sourceLabel(source),
            values: labels.map(() => 0),
            total: 0,
        };
        const data = sourceData(source, basis);

        labels.forEach((label, index) => {
            const value = Number(data[label] ?? 0);
            existing.values[index] += value;
            existing.total += value;
        });

        channels.set(key, existing);
    });

    return Array.from(channels.values()).sort((a, b) => b.total - a.total);
}

function buildTopCampaignSeries(graphData, basis, labels, metricData = null, limit = 8) {
    const metricByCampaign = new Map((metricData?.dataSources ?? []).map((source) => [
        source.campaignId || source.campaignRemoteId || source.campaignName,
        source,
    ]));

    return (graphData?.dataSources ?? [])
        .map((source) => {
            const data = sourceData(source, basis);
            const metricSource = metricByCampaign.get(source.campaignId || source.campaignRemoteId || source.campaignName);
            const metricValues = sourceData(metricSource, basis);
            const total = labels.reduce((sum, label) => sum + Number(metricValues[label] ?? data[label] ?? 0), 0);

            return {
                id: source.campaignId || source.campaignRemoteId || source.campaignName,
                label: source.campaignName || "-",
                channel: sourceLabel(source),
                values: labels.map((label) => Number(data[label] ?? 0)),
                total,
            };
        })
        .filter((series) => series.total > 0)
        .sort((a, b) => b.total - a.total)
        .slice(0, limit);
}

function weightedCpcByChannel(cpcData, clicksData, basis, labels) {
    const clicksByCampaign = new Map((clicksData?.dataSources ?? []).map((source) => [
        source.campaignId || source.campaignRemoteId || source.campaignName,
        source,
    ]));
    const channels = new Map();

    (cpcData?.dataSources ?? []).forEach((source) => {
        const key = sourceKey(source);
        const existing = channels.get(key) ?? {
            key,
            label: sourceLabel(source),
            weighted: labels.map(() => 0),
            clicks: labels.map(() => 0),
            totalClicks: 0,
        };
        const cpc = moneySourceData(source);
        const clicks = sourceData(clicksByCampaign.get(source.campaignId || source.campaignRemoteId || source.campaignName), basis);

        labels.forEach((label, index) => {
            const clickCount = Number(clicks[label] ?? 0);
            const cpcMajor = moneyAmount(cpc[label]) / 100;

            existing.weighted[index] += cpcMajor * clickCount;
            existing.clicks[index] += clickCount;
            existing.totalClicks += clickCount;
        });

        channels.set(key, existing);
    });

    return Array.from(channels.values())
        .map((channel) => ({
            ...channel,
            values: labels.map((_, index) => channel.clicks[index] > 0 ? channel.weighted[index] / channel.clicks[index] : null),
        }))
        .filter((channel) => channel.totalClicks > 0)
        .sort((a, b) => b.totalClicks - a.totalClicks);
}

function legendStyle() {
    return {
        bottom: 0,
        left: "center",
        type: "plain",
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };
}

function lineSeries(rows, type = "line") {
    return rows.map((row, index) => ({
        name: row.label,
        type,
        stack: type === "bar" ? "total" : undefined,
        smooth: type === "line",
        symbolSize: type === "line" ? 6 : undefined,
        barMaxWidth: type === "bar" ? 36 : undefined,
        data: row.values,
        itemStyle: {color: ChannelMixColors[index % ChannelMixColors.length]},
        lineStyle: {color: ChannelMixColors[index % ChannelMixColors.length]},
    }));
}

export default function ReportingMarketingTrendsLab({project}) {
    const {t} = useTranslation();
    const {since, until, centresAllowListed, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const centreFilterKey = (centresAllowListed ?? []).join("|");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");

    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: centresAllowListed,
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, centreFilterKey, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setLoading(true);
        setData(null);

        const api = new ErpCalculationApi(getAuthorityConfiguration());

        api.reportingMarketingTrends({projectId: project.id, erpFilter})
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setData(nextData);
                setLoading(false);
            })
            .catch((error) => {
                if (!active) {
                    return;
                }

                et(error).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project?.id, erpFilter]);

    const labels = data?.orders?.labels ?? data?.clicks?.labels ?? data?.pno?.labels ?? data?.cpc?.labels ?? [];
    const xLabels = labels.map(formatDateLabel);
    const currency = data?.cpc?.dataSources?.find((source) => Object.values(source.data ?? {})[0]?.currency)?.data
        ? Object.values(data.cpc.dataSources.find((source) => Object.values(source.data ?? {})[0]?.currency).data ?? {})[0]?.currency
        : projectCurrency(project);
    const ordersByChannel = useMemo(() => sumNumberSeriesByChannel(data?.orders, "marketing", labels).slice(0, 8), [data, labels.join("|")]);
    const clicksByChannel = useMemo(() => sumNumberSeriesByChannel(data?.clicks, "marketing", labels).slice(0, 8), [data, labels.join("|")]);
    const cpcByChannel = useMemo(() => weightedCpcByChannel(data?.cpc, data?.clicks, "marketing", labels).slice(0, 8), [data, labels.join("|")]);
    const pnoTopCampaigns = useMemo(() => buildTopCampaignSeries(data?.pno, "marketing", labels, data?.orders, 8), [data, labels.join("|")]);
    const totalRows = data?.totals?.rows ?? [];
    const hasProfitabilityData = totalRows.some((row) => row.goodsMargin != null);
    const completeTotals = totalRows.reduce((sum, row) => {
        sum.spend += moneyAmount(row.spend);
        sum.turnover += moneyAmount(row.turnover);
        sum.goodsMargin += moneyAmount(row.goodsMargin);
        sum.orders += Number(row.orders ?? 0);
        sum.clicks += Number(row.clicks ?? 0);
        sum.impressions += Number(row.impressions ?? 0);
        return sum;
    }, {spend: 0, turnover: 0, goodsMargin: 0, orders: 0, clicks: 0, impressions: 0});
    const completePoas = hasProfitabilityData && completeTotals.spend > 0
        ? completeTotals.goodsMargin / completeTotals.spend * 100
        : null;
    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const grid = {left: 14, right: 18, top: 28, bottom: 58, containLabel: true};

    const ordersOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid,
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, formatChartNumber),
        },
        legend: legendStyle(),
        xAxis: {type: "category", data: xLabels},
        yAxis: {type: "value", axisLabel: {fontSize: 12, formatter: formatChartNumber}},
        series: lineSeries(ordersByChannel, "bar"),
    };

    const clicksOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid,
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, formatChartNumber),
        },
        legend: legendStyle(),
        xAxis: {type: "category", data: xLabels},
        yAxis: {type: "value", axisLabel: {fontSize: 12, formatter: formatChartNumber}},
        series: lineSeries(clicksByChannel, "bar"),
    };

    const pnoOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid,
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, formatChartPercent),
        },
        legend: legendStyle(),
        xAxis: {type: "category", data: xLabels},
        yAxis: {type: "value", axisLabel: {fontSize: 12, formatter: formatChartPercent}},
        series: lineSeries(pnoTopCampaigns),
    };

    const cpcOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid,
        tooltip: {
            trigger: "axis",
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        legend: legendStyle(),
        xAxis: {type: "category", data: xLabels},
        yAxis: {
            type: "value",
            name: currencyUnit(currency),
            nameTextStyle: chartTextStyle,
            axisLabel: {fontSize: 12, formatter: (value) => formatChartCurrency(value, currency)},
        },
        series: lineSeries(cpcByChannel),
    };

    const financialTrendOption = {
        color: ["#7c3aed", "#2563eb"],
        textStyle: chartTextStyle,
        grid,
        tooltip: {trigger: "axis", formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency))},
        legend: legendStyle(),
        xAxis: {type: "category", data: totalRows.map((row) => formatDateLabel(row.date))},
        yAxis: {type: "value", axisLabel: {fontSize: 12, formatter: formatChartNumber}},
        series: [
            {name: t("reporting_lab.table.marketing", {defaultValue: "Marketing"}), type: "bar", data: totalRows.map((row) => moneyAmount(row.spend) / 100), barMaxWidth: 30},
            {name: t("reporting_lab.table.revenue", {defaultValue: "Obrat"}), type: "line", smooth: true, data: totalRows.map((row) => moneyAmount(row.turnover) / 100)},
        ].filter((series) => hasProfitabilityData || series.name !== "POAS"),
    };

    const efficiencyTrendOption = {
        color: ["#f97316", "#16a34a", "#0ea5e9", "#e11d48"],
        textStyle: chartTextStyle,
        grid,
        tooltip: {trigger: "axis", formatter: (params) => formatAxisTooltip(params, [formatChartPercent, formatChartMultiplier, formatChartPercent, formatChartPercent])},
        legend: legendStyle(),
        xAxis: {type: "category", data: totalRows.map((row) => formatDateLabel(row.date))},
        yAxis: {type: "value", axisLabel: {fontSize: 12, formatter: formatChartPercent}},
        series: [
            {name: "PNO", type: "line", smooth: true, data: totalRows.map((row) => row.pno)},
            {name: "POAS", type: "line", smooth: true, data: totalRows.map((row) => row.poas)},
            {name: "CTR", type: "line", smooth: true, data: totalRows.map((row) => row.ctr)},
            {name: t("reporting_lab.table.conversion_rate", {defaultValue: "Konverze"}), type: "line", smooth: true, data: totalRows.map((row) => row.conversionRate)},
        ].filter((series) => hasProfitabilityData || series.name !== "POAS"),
    };

    return <WideSecureLayout
        title={t("reporting_lab.pages.marketing_trends", {defaultValue: "Marketingové trendy"})}
        actions={<OrderDashboardFilters hideFilters={false} showMarketingFilters={false} showModeSwitch={false}/>}
        alerts={[snapshotCompletenessAlert(data?.snapshot, t)]}
    >
        <div className="grid grid-cols-12 gap-4">
                <ReportingPageDescription
                    className="col-span-12"
                    title={t("reporting_lab.descriptions.marketing_trends.title", {defaultValue: "K čemu slouží marketingové trendy"})}
                >
                    {t("reporting_lab.descriptions.marketing_trends.text", {defaultValue: "Sleduje vývoj objednávek, kliků, PNO a CPC podle kanálů a kampaní, aby šlo rychle poznat změnu výkonu v čase."})}
                </ReportingPageDescription>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.marketing.costs", {defaultValue: "Marketingové náklady"})} value={renderCurrencyValue({amount: completeTotals.spend, currency}, true)} meta={t("reporting_lab.marketing_trends.complete_scope", {defaultValue: "Všechny měřené marketingové kanály"})}/>
                </div>
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.kpi.attributed_revenue", {defaultValue: "Přiřazený obrat"})} value={renderCurrencyValue({amount: completeTotals.turnover, currency}, true)} meta={`${t("reporting_lab.kpi.orders", {defaultValue: "Objednávky"})}: ${renderNumber(completeTotals.orders)}`}/>
                </div>
                {hasProfitabilityData && <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label="POAS" value={completePoas === null ? "-" : formatChartMultiplier(completePoas)} meta={t("reporting_lab.metrics_help.poas", {defaultValue: "Marže zboží dělená marketingovými náklady."})}/>
                </div>}
                <div className="col-span-12 md:col-span-6 xl:col-span-3">
                    <KpiCard loading={loading} label={t("reporting_lab.table.impressions", {defaultValue: "Imprese"})} value={renderNumber(completeTotals.impressions)} meta={`${t("reporting_lab.marketing_trends.clicks", {defaultValue: "Kliky"})}: ${renderNumber(completeTotals.clicks)}`}/>
                </div>

                <div className="col-span-12 xl:col-span-6">
                    <Panel loading={loading} title={t("reporting_lab.marketing_trends.spend_revenue_trend", {defaultValue: "Marketingové náklady a obrat v čase"})}>
                        <EChart option={financialTrendOption} height={360}/>
                    </Panel>
                </div>
                <div className="col-span-12 xl:col-span-6">
                    <Panel loading={loading} title={t("reporting_lab.marketing_trends.efficiency_trend", {defaultValue: "Vývoj efektivity marketingu"})}>
                        <EChart option={efficiencyTrendOption} height={360}/>
                    </Panel>
                </div>

                <div className="col-span-12 xl:col-span-6">
                    <Panel loading={loading} title={t("reporting_lab.marketing_trends.orders_by_channel", {defaultValue: "Objednávky podle kanálu"})}>
                        <EChart option={ordersOption} height={360}/>
                    </Panel>
                </div>
                <div className="col-span-12 xl:col-span-6">
                    <Panel loading={loading} title={t("reporting_lab.marketing_trends.clicks_by_channel", {defaultValue: "Kliky podle kanálu"})}>
                        <EChart option={clicksOption} height={360}/>
                    </Panel>
                </div>
                <div className="col-span-12 xl:col-span-6">
                    <Panel loading={loading} title={t("reporting_lab.marketing_trends.pno_top_campaigns", {defaultValue: "PNO top 8 kampaní podle objednávek"})}>
                        <EChart option={pnoOption} height={360}/>
                    </Panel>
                </div>
                <div className="col-span-12 xl:col-span-6">
                    <Panel loading={loading} title={t("reporting_lab.marketing_trends.cpc_by_channel", {defaultValue: "CPC podle kanálu"})}>
                        <EChart option={cpcOption} height={360}/>
                    </Panel>
                </div>
            </div>
    </WideSecureLayout>;
}
